import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { SLOT_QUEUE } from "../../data/mockData";
import { CheckCircle2, XCircle, Clock, Users, CalendarClock, CheckCheck } from "lucide-react";

function buildQueue(vertical, config) {
  if (vertical === "enrichment") return SLOT_QUEUE.map((q) => ({ ...q }));
  if (vertical === "aesthetic") {
    return config.leads.filter((l) => ["qualified", "consult_booked"].includes(l.stage)).slice(0, 10).map((l, i) => ({
      id: `AQ-${l.contact_id}`,
      lead: l,
      type: l.stage === "qualified" ? "approval" : "offered",
      request: l.stage === "qualified" ? `Consult request — ${l.programme_name}` : "Consult slot held — awaiting confirmation",
      suggested_slot: ["Weekday 11am", "Weekday 3pm", "Sat morning"][i % 3],
      capacity: l.stage === "qualified" ? "Doctor calendar open" : "Held for 24h",
      waiting_since: l.last_activity_date,
    }));
  }
  return config.leads.filter((l) => ["recall_due", "contacted"].includes(l.stage)).slice(0, 12).map((l, i) => ({
    id: `DQ-${l.contact_id}`,
    lead: l,
    type: l.stage === "recall_due" ? "approval" : "offered",
    request: l.stage === "recall_due" ? `Recall booking — ${l.programme_name}` : "Recall slot offered — awaiting reply",
    suggested_slot: ["Tue 9:30am", "Wed 2pm", "Fri 11am", "Sat 10am"][i % 4],
    capacity: "Hygienist available",
    waiting_since: l.last_activity_date,
  }));
}

export default function SlotQueue() {
  const { vertical, config, branchFilter } = useOutletContext();
  const [queue, setQueue] = useState([]);
  const [handled, setHandled] = useState(0);

  useEffect(() => {
    setQueue(buildQueue(vertical, config));
    setHandled(0);
  }, [vertical, config]);

  const visible = queue.filter((q) => branchFilter === "all" || q.lead.branch_name === branchFilter);
  const approvals = visible.filter((q) => q.type === "approval");
  const offered = visible.filter((q) => q.type === "offered");

  const approve = (item) => {
    setQueue((prev) => prev.filter((q) => q.id !== item.id));
    setHandled((h) => h + 1);
    toast.success(`Slot approved — offer sent to ${item.lead.parent_name} via WhatsApp with a 24h hold`);
  };
  const decline = (item) => {
    setQueue((prev) => prev.filter((q) => q.id !== item.id));
    setHandled((h) => h + 1);
    toast(`${item.lead.parent_name} moved to waitlist — they'll get first offer when a seat opens`);
  };
  const nudge = (item) => toast.success(`Reminder sent to ${item.lead.parent_name} — hold expires soon`);

  const waitingTime = (iso) => { const minutes = Math.max(1, Math.round((Date.now() - new Date(iso)) / 6e4)); return minutes < 60 ? `${minutes}m` : `${Math.round(minutes / 60)}h`; };

  return (
    <div className="space-y-5 animate-fade-up max-w-5xl">
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Awaiting approval", value: approvals.length, icon: Clock },
          { label: "Offers pending reply", value: offered.length, icon: CalendarClock },
          { label: "Handled this session", value: handled, icon: CheckCheck },
        ].map((k) => (
          <Card key={k.label} className="rounded-2xl border-border shadow-[var(--shadow-sm)]">
            <CardContent className="p-4">
              <k.icon className="h-4 w-4 text-accent mb-1.5" />
              <p className="text-xl font-heading font-semibold tabular-nums">{k.value}</p>
              <p className="text-[11px] text-muted-foreground">{k.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Approval queue */}
      <div data-testid="slot-coordination-queue">
        <div className="flex items-center gap-2 mb-3">
          <h2 className="font-heading font-semibold text-foreground">Needs staff approval</h2>
          <Badge variant="secondary" className="tabular-nums">{approvals.length}</Badge>
        </div>
        <p className="text-xs text-muted-foreground mb-4 max-w-2xl">
          {vertical === "enrichment"
            ? "Qualified parents waiting on a vacancy / fit check. Approve to send the slot offer with a 24-hour hold — decline routes them to waitlist recovery instead of losing them."
            : "Requests waiting on staff confirmation before a slot is committed."}
        </p>
        <div className="space-y-3">
          {approvals.length === 0 && (
            <Card className="rounded-2xl border-border"><CardContent className="p-8 text-center">
              <CheckCircle2 className="h-8 w-8 text-teal-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-foreground">Queue clear — nothing waiting on approval</p>
            </CardContent></Card>
          )}
          {approvals.map((item) => (
            <Card key={item.id} className="rounded-2xl border-border shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] transition-shadow">
              <CardContent className="p-4 sm:p-5">
                <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/demo/leads/${item.lead.contact_id}`} className="text-sm font-semibold text-foreground hover:text-accent transition-colors" data-testid={`slot-queue-lead-${item.lead.contact_id}`}>
                        {item.lead.parent_name}{item.lead.child_name ? ` · ${item.lead.child_name}` : ""}
                      </Link>
                      <Badge variant="secondary" className="text-[10px]">{item.lead.branch_name}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{item.request}</p>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[11px] text-muted-foreground">
                      <span className="inline-flex items-center gap-1"><CalendarClock className="h-3 w-3" /> Preferred: {item.suggested_slot}</span>
                      <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {item.capacity}</span>
                      <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> Waiting {waitingTime(item.waiting_since)}</span>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" className="rounded-xl" onClick={() => approve(item)} data-testid={`slot-approve-button-${item.lead.contact_id}`}>
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Approve & offer
                    </Button>
                    <Button size="sm" variant="secondary" className="rounded-xl border border-border" onClick={() => decline(item)} data-testid={`slot-decline-button-${item.lead.contact_id}`}>
                      <XCircle className="h-3.5 w-3.5 mr-1.5" /> No vacancy
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Offers pending */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <h2 className="font-heading font-semibold text-foreground">Offers pending parent reply</h2>
          <Badge variant="secondary" className="tabular-nums">{offered.length}</Badge>
        </div>
        <div className="space-y-3">
          {offered.length === 0 && <p className="text-sm text-muted-foreground">No offers pending.</p>}
          {offered.map((item) => (
            <Card key={item.id} className="rounded-2xl border-border shadow-[var(--shadow-sm)]">
              <CardContent className="p-4 sm:p-5 flex flex-col md:flex-row md:items-center gap-4 justify-between">
                <div className="min-w-0">
                  <Link to={`/demo/leads/${item.lead.contact_id}`} className="text-sm font-semibold text-foreground hover:text-accent transition-colors">
                    {item.lead.parent_name}{item.lead.child_name ? ` · ${item.lead.child_name}` : ""}
                  </Link>
                  <p className="text-xs text-muted-foreground mt-1">{item.request} · {item.suggested_slot} · {item.capacity}</p>
                </div>
                <Button size="sm" variant="secondary" className="rounded-xl border border-border shrink-0" onClick={() => nudge(item)} data-testid={`slot-nudge-button-${item.lead.contact_id}`}>
                  Send reminder
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
